import React, { FC, useState, useMemo, useRef, useEffect, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

import { ArticleItem } from '@app/help-page/blocks/types';

const stripHtml = (html: string): string =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildSearchText = (item: ArticleItem): string => {
  const title = item.title || '';
  const description = typeof item.description === 'string' ? item.description : '';
  return `${title} ${stripHtml(description)}`.toLowerCase();
};

const getSnippet = (
  text: string,
  query: string,
  length = 120,
): { before: string; match: string; after: string } => {
  const normalized = text.toLowerCase();
  const queryLower = query.toLowerCase();
  const index = normalized.indexOf(queryLower);
  if (index === -1) {
    const snippet = text.slice(0, length).trim() + (text.length > length ? '…' : '');
    return { before: snippet, match: '', after: '' };
  }
  const start = Math.max(0, index - 30);
  const end = Math.min(text.length, index + length - 30);
  const snippet = text.slice(start, end).trim();
  const snippetLower = snippet.toLowerCase();
  const highlightIndex = snippetLower.indexOf(queryLower);
  if (highlightIndex !== -1) {
    const before = snippet.slice(0, highlightIndex);
    const match = snippet.slice(highlightIndex, highlightIndex + query.length);
    const after = snippet.slice(highlightIndex + query.length);
    return {
      before: (start > 0 ? '…' : '') + before,
      match,
      after: after + (end < text.length ? '…' : ''),
    };
  }
  return {
    before: (start > 0 ? '…' : '') + snippet + (end < text.length ? '…' : ''),
    match: '',
    after: '',
  };
};

const getItemUrl = (item: ArticleItem): string => `/support/${item.id}`;

interface SearchBarProps {
  menuList: ArticleItem[];
}

const SearchBar: FC<SearchBarProps> = ({ menuList }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchIndex = useMemo(
    () =>
      menuList.map((item) => ({
        id: item.id,
        title: item.title || '',
        searchText: buildSearchText(item),
        description: typeof item.description === 'string' ? stripHtml(item.description) : '',
      })),
    [menuList],
  );

  const results = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return [];

    const terms = normalizedQuery.split(/\s+/).filter(Boolean);
    const matched = searchIndex.filter((item) =>
      terms.every((term) => item.searchText.includes(term)),
    );

    return matched.slice(0, 10).map((item) => ({
      ...item,
      snippet: getSnippet(item.description, normalizedQuery),
    }));
  }, [query, searchIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (query.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const hasResults = results.length > 0;

  return (
    <div className="relative w-full max-w-[680px]" ref={containerRef}>
      <div className="flex items-center gap-2 rounded-[10px] border border-gray-300 bg-white px-3 py-2 shadow-sm">
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-500"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          className="w-full bg-transparent text-sm text-black outline-none placeholder:text-gray-400"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="Поиск по базе знаний"
          aria-label="Поиск по базе знаний"
        />
      </div>

      {isOpen && query.trim() && (
        <div className="absolute left-0 right-0 z-50 mt-2 max-h-[320px] overflow-y-auto rounded-[10px] border border-gray-200 bg-white shadow-large">
          {hasResults ? (
            <ul className="divide-y divide-gray-100">
              {results.map((item) => (
                <li key={item.id} className="hover:bg-gray-50">
                  <Link
                    to={getItemUrl({ id: item.id, title: item.title } as ArticleItem)}
                    className="block px-4 py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="text-sm font-semibold text-black">{item.title}</div>
                    {item.snippet ? (
                      <p className="mt-1 text-[13px] leading-5 text-gray-600">
                        {item.snippet.before}
                        {item.snippet.match && (
                          <mark className="bg-yellow-200">{item.snippet.match}</mark>
                        )}
                        {item.snippet.after}
                      </p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-sm text-gray-600">Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
